import useTranslation from "@babackapp/translations/use-translation";
import useRemoveLocation from "@babackapp/private-api-client/client/use-delete-shop-location";
import useUpdateLocation from "@babackapp/private-api-client/client/use-update-shop-location";
import LocationForm, { LocationFormValues } from "./location-form";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { PropsWithChildren, useCallback } from "react";
import { ShopLocationDto } from "@babackapp/private-api-client/types";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@babackapp/ui/components/dialog";

type UpdateLocationDialogProps = {
  location: ShopLocationDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function UpdateLocationDialog({
  children,
  location,
  open,
  onOpenChange,
}: PropsWithChildren<UpdateLocationDialogProps>) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateLocation } = useUpdateLocation(shop, location.id!);
  const { mutateAsync: removeLocation } = useRemoveLocation(shop);

  const onSubmit = useCallback(
    (data: ShopLocationDto) => {
      if (!shop?.id || !location?.id) return;
      onOpenChange(false);
      return updateLocation(data);
    },
    [location.id, shop.id, updateLocation],
  );

  const onRemoveLocation = useCallback(() => {
    if (!shop?.id || !location?.id) return;
    onOpenChange(false);
    return removeLocation(location.id);
  }, [location.id, shop.id, removeLocation]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <div>{children}</div>
      </DialogTrigger>
      <DialogContent className="pt-10">
        <DialogHeader>
          <DialogTitle>
            {t("settings.shipping.updateLocationTitle")}
          </DialogTitle>
        </DialogHeader>
        <LocationForm
          onSubmit={onSubmit}
          onRemove={onRemoveLocation}
          defaultValues={
            {
              address1: location.address?.address1,
              address2: location.address?.address2,
              city: location.address?.city,
              province_code: location.address?.province_code,
              country: {
                name: location.address?.country_name,
                code: location.address?.country_code,
              },
              isDefault: location.isDefault,
              zip: location.address?.zip,
              companyName: location.address?.companyName,
              phone: location.address?.phone ?? "",
            } as LocationFormValues
          }
        />
      </DialogContent>
    </Dialog>
  );
}
